// src/pages/banner-module/UpdateBanner.js

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import DashboardWrapper from "../../../components/DashboardWrapper";
import CrudForm from "../../../components/dashboard/form/CrudForm";
import bannerService from "../../../services/bannerService";

export default function UpdateBanner() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Cache key per banner
  const localCacheKey = `bannerEditCache_${id}`;

  const allowedFields = [
    "banner",
    "banner_title",
    "banner_content",
    "starting_price",
    "status",
  ];

  // -----------------------------
  // Load banner data
  // -----------------------------
  useEffect(() => {
    const loadBanner = async () => {
      try {
        setLoading(true);

        const res = await bannerService.getBannerById(id);
        const banner = res.data?.data || res.data;

        const normalized = {
          banner: banner.banner || null,
          banner_title: banner.banner_title || "",
          banner_content: banner.banner_content || "",
          starting_price: banner.starting_price || "",
          status: banner.status ?? 1,
        };

        setInitialData(normalized);
        localStorage.setItem(localCacheKey, JSON.stringify(normalized));
      } catch (error) {
        console.error("Failed to load banner:", error);
        Swal.fire("Error", "Failed to load banner details.", "error");
        navigate("/admin/sliders");
      } finally {
        setLoading(false);
      }
    };

    // Try cache first
    const cached = localStorage.getItem(localCacheKey);
    if (cached) {
      setInitialData(JSON.parse(cached));
      setLoading(false);
    }

    loadBanner();
  }, [id, navigate, localCacheKey]);

  // -----------------------------
  // Handle submit
  // -----------------------------
const handleSubmit = useCallback(
  async (formData) => {
    try {
      setLoading(true);

      const payload = {};

      allowedFields.forEach((field) => {
        if (field === "banner") {
          // Only include new file
          if (formData.banner instanceof File) {
            payload.banner = formData.banner;
          }
          // Do NOT send old banner string
        } else {
          payload[field] = formData[field] ?? "";
        }
      });

      await bannerService.updateBanner(id, payload);

      Swal.fire({
        title: "Success",
        text: "Banner updated successfully!",
        icon: "success",
        confirmButtonColor: "#214b7a",
      });

      localStorage.removeItem(localCacheKey);
      navigate("/admin/sliders");
    } catch (error) {
      console.error("Update banner error:", error);

      const message =
        error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(", ")
          : error.response?.data?.message || "Failed to update banner";

      Swal.fire("Error", message, "error");
    } finally {
      setLoading(false);
    }
  },
  [id, navigate, allowedFields, localCacheKey]
);


  // -----------------------------
  // Render
  // -----------------------------
  if (loading) {
    return (
      <DashboardWrapper>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "300px" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </DashboardWrapper>
    );
  }

  if (!initialData) {
    return (
      <DashboardWrapper>
        <div className="alert alert-danger">Banner data not found.</div>
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper>
      <div className="p-4">
        <h1 className="h4 mb-4">Update Banner</h1>

        <button
          className="btn btn-secondary mb-3"
          onClick={() => navigate(-1)}
        >
          Back
        </button>

        <div className="card">
          <h5 className="card-header">Edit Banner</h5>
          <div className="card-body">
            <CrudForm
              initialData={initialData}
              onSubmit={handleSubmit}
              allowedFields={allowedFields}
              fileFields={[{ name: "banner", multiple: false }]}
            />
          </div>
        </div>
      </div>
    </DashboardWrapper>
  );
}
